import {
  getMe,
  updateUser,
  changePassword, // NEW
  getSmtpConfigs,
  addSmtpConfig,
  updateSmtpConfig,
  deleteSmtpConfig,
  testSmtpConnection,
  sendSmtpTestEmail,

  getTwilioConfigs,
  addTwilioConfig,
  updateTwilioConfig,
  deleteTwilioConfig,
  testTwilioConnection,
  sendTwilioTestMessage,
  getVoices,
  cloneVoice,
} from "../api/SettingsApi";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  User, Mail, Building, Shield, Bell, Lock,
  Palette, Save, LogOut, Server, Plus, Trash2, Edit, CheckCircle2, XCircle, Send, TestTube,
  MessageSquare, Phone
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";

const SmtpSettings = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = {
    name: "",
    host: "",
    port: 587,
    username: "",
    password: "",
    use_tls: true,
    use_ssl: false,
    from_email: "",
    from_name: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [testingId, setTestingId] = useState(null);
  const [testEmailDialog, setTestEmailDialog] = useState(null);
  const [testEmailTo, setTestEmailTo] = useState("");

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await getSmtpConfigs();
      setConfigs(res.data.results || []);
    } catch (e) {
      toast.error("Failed to load SMTP configs");
    }
    setLoading(false);
  };

  useEffect(() => { fetchConfigs(); }, []);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "",
      host: c.host || "",
      port: c.port || 587,
      username: c.username || "",
      password: "",
      use_tls: c.use_tls !== false,
      use_ssl: !!c.use_ssl,
      from_email: c.from_email || "",
      from_name: c.from_name || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password; // keep existing
        await updateSmtpConfig(editingId, payload);
        toast.success("SMTP config updated");
      } else {
        await addSmtpConfig(form);
        toast.success("SMTP config created");
      }
      setOpen(false);
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this SMTP configuration?")) return;
    try {
      await deleteSmtpConfig(id);
      toast.success("Deleted");
      fetchConfigs();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const testConnection = async (id) => {
    setTestingId(id);
    try {
      const res = await testSmtpConnection(id);
      if (res.data.success) toast.success(res.data.message || "Connection OK");
      else toast.error(res.data.message || "Connection failed");
      fetchConfigs();
    } catch (e) {
      toast.error("Test request failed");
    }
    setTestingId(null);
  };

  const sendTestEmail = async () => {
    if (!testEmailTo) { toast.error("Enter recipient email"); return; }
    try {
      const res = await sendSmtpTestEmail(testEmailDialog, testEmailTo);
      if (res.data.success) toast.success("Test email sent");
      else toast.error(res.data.message || "Send failed");
      setTestEmailDialog(null);
      setTestEmailTo("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  return (
    <Card className="border border-gray-100 shadow-sm" data-testid="smtp-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Server size={20} className="text-[#8c3add]" />
            SMTP Configurations
          </CardTitle>
          <Button onClick={openCreate} size="sm" className="bg-[#8c3add] hover:bg-[#702eb0] gap-2" data-testid="add-smtp-btn">
            <Plus size={16} /> Add SMTP
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : configs.length === 0 ? (
          <div className="p-8 text-center text-gray-500 border border-dashed rounded-lg">
            <Server size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No SMTP server configured. Add one to send real phishing emails.</p>
          </div>
        ) : (
          Array.isArray(configs) && configs.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`smtp-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-[#1f1f23] truncate">{c.name}</p>
                  {c.test_result === "success" && (
                    <Badge className="bg-green-100 text-green-700 gap-1"><CheckCircle2 size={12} /> Verified</Badge>
                  )}
                  {c.test_result === "failed" && (
                    <Badge className="bg-red-100 text-red-700 gap-1"><XCircle size={12} /> Failed</Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {c.host}:{c.port} • {c.username} • From: {c.from_email}
                </p>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2"
                  onClick={() => testConnection(c.id)}
                  disabled={testingId === c.id}
                  data-testid={`test-smtp-${c.id}`}
                  title="Test connection"
                >
                  <TestTube size={16} className="text-[#1fa7a1]" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2"
                  onClick={() => { setTestEmailDialog(c.id); setTestEmailTo(""); }}
                  title="Send test email"
                  data-testid={`send-test-${c.id}`}
                >
                  <Send size={16} className="text-[#8c3add]" />
                </Button>
                <Button variant="ghost" size="sm" className="p-2" onClick={() => openEdit(c)} title="Edit">
                  <Edit size={16} className="text-gray-600" />
                </Button>
                <Button variant="ghost" size="sm" className="p-2 hover:bg-red-50" onClick={() => remove(c.id)} title="Delete">
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>

      {/* Create/Edit SMTP dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="smtp-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit SMTP Configuration" : "Add SMTP Configuration"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div className="space-y-2">
              <Label>Config Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="smtp-name-input" placeholder="Primary SMTP" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-2">
                <Label>Host</Label>
                <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} required data-testid="smtp-host-input" placeholder="smtp.gmail.com" />
              </div>
              <div className="space-y-2">
                <Label>Port</Label>
                <Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: parseInt(e.target.value || 0) })} required data-testid="smtp-port-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required data-testid="smtp-username-input" />
            </div>
            <div className="space-y-2">
              <Label>Password {editingId && <span className="text-xs text-gray-400">(leave blank to keep existing)</span>}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editingId} data-testid="smtp-password-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>From Email</Label>
                <Input type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} required data-testid="smtp-from-email-input" />
              </div>
              <div className="space-y-2">
                <Label>From Name</Label>
                <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} data-testid="smtp-from-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <Label className="text-sm">Use TLS</Label>
                <Switch checked={form.use_tls} onCheckedChange={(v) => setForm({ ...form, use_tls: v, use_ssl: v ? false : form.use_ssl })} />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <Label className="text-sm">Use SSL</Label>
                <Switch checked={form.use_ssl} onCheckedChange={(v) => setForm({ ...form, use_ssl: v, use_tls: v ? false : form.use_tls })} />
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#8c3add] hover:bg-[#702eb0]" data-testid="smtp-submit-btn">
                {editingId ? "Save Changes" : "Add SMTP"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send test email dialog */}
      <Dialog open={!!testEmailDialog} onOpenChange={(o) => !o && setTestEmailDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Label>Recipient Email</Label>
            <Input type="email" placeholder="you@example.com" value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)} data-testid="test-email-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailDialog(null)}>Cancel</Button>
            <Button className="bg-[#8c3add] hover:bg-[#702eb0]" onClick={sendTestEmail} data-testid="send-test-confirm-btn">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const TwilioSettings = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = {
    name: "", account_sid: "", auth_token: "",
    sms_from: "", whatsapp_from: "whatsapp:+14155238886",
  };
  const [form, setForm] = useState(emptyForm);
  const [testingId, setTestingId] = useState(null);
  const [sendDialog, setSendDialog] = useState(null); // {configId, channel}
  const [sendTo, setSendTo] = useState("");
  const [sendBody, setSendBody] = useState("Test message from Culsight Phishing Simulation Tool.");

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await getTwilioConfigs();
      setConfigs(res.data.results || []);
    } catch (e) { toast.error("Failed to load Twilio configs"); }
    setLoading(false);
  };

  useEffect(() => { fetchConfigs(); }, []);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "",
      account_sid: c.account_sid || "",
      auth_token: "",
      sms_from: c.sms_from || "",
      whatsapp_from: c.whatsapp_from || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.auth_token) delete payload.auth_token;
        await updateTwilioConfig(editingId, payload);
        toast.success("Twilio config updated");
      } else {
        await addTwilioConfig(form);
        toast.success("Twilio config created");
      }
      setOpen(false);
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this Twilio configuration?")) return;
    try {
      await deleteTwilioConfig(id);
      toast.success("Deleted");
      fetchConfigs();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const testConnection = async (id) => {
    setTestingId(id);
    try {
      const res = await testTwilioConnection(id);
      if (res.data.success) toast.success(res.data.message || "Connected");
      else toast.error(res.data.message || "Test failed");
      fetchConfigs();
    } catch (e) { toast.error("Test request failed"); }
    setTestingId(null);
  };

  const submitSendTest = async () => {
    if (!sendDialog || !sendTo) { toast.error("Enter recipient phone"); return; }
    try {
      const res = await sendTwilioTestMessage(
        sendDialog.configId,
        sendTo,
        sendDialog.channel,
        sendBody
      );
      if (res.data.success) {
        toast.success(res.data.mocked ? "Mock send OK (no live credentials)" : `Sent! SID: ${res.data.sid}`);
      } else {
        toast.error(res.data.message || "Send failed");
      }
      setSendDialog(null); setSendTo("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
  };

  return (
    <Card className="border border-gray-100 shadow-sm" data-testid="twilio-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <MessageSquare size={20} className="text-[#1fa7a1]" />
            Twilio (SMS + WhatsApp)
          </CardTitle>
          <Button onClick={openCreate} size="sm" className="bg-[#1fa7a1] hover:bg-[#178a85] gap-2" data-testid="add-twilio-btn">
            <Plus size={16} /> Add Twilio
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : configs.length === 0 ? (
          <div className="p-8 text-center text-gray-500 border border-dashed rounded-lg">
            <MessageSquare size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No Twilio account connected.</p>
            <p className="text-xs mt-1">SMS &amp; WhatsApp campaigns will run in <strong>MOCK mode</strong> until configured.</p>
            <p className="text-xs mt-2 text-gray-400">Get free trial credentials at <a href="https://www.twilio.com/console" target="_blank" rel="noreferrer" className="text-[#8c3add] underline">twilio.com/console</a></p>
          </div>
        ) : (
          Array.isArray(configs) && configs.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`twilio-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-[#1f1f23] truncate">{c.name}</p>
                  {c.test_result === "success" && <Badge className="bg-green-100 text-green-700 gap-1"><CheckCircle2 size={12} /> Verified</Badge>}
                  {c.test_result === "failed" && <Badge className="bg-red-100 text-red-700 gap-1"><XCircle size={12} /> Failed</Badge>}
                </div>
                <p className="text-xs text-gray-500 truncate">
                  SID: {c.account_sid?.slice(0, 10)}…{c.account_sid?.slice(-4)}
                  {c.sms_from && <> • SMS: <code>{c.sms_from}</code></>}
                  {c.whatsapp_from && <> • WA: <code>{c.whatsapp_from}</code></>}
                </p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="p-2" onClick={() => testConnection(c.id)} disabled={testingId === c.id} title="Verify credentials" data-testid={`test-twilio-${c.id}`}>
                  <TestTube size={16} className="text-[#1fa7a1]" />
                </Button>
                <Button variant="ghost" size="sm" className="p-2" onClick={() => { setSendDialog({ configId: c.id, channel: "sms" }); setSendTo(""); }} title="Send test SMS">
                  <Send size={16} className="text-[#8c3add]" />
                </Button>
                <Button variant="ghost" size="sm" className="p-2" onClick={() => { setSendDialog({ configId: c.id, channel: "whatsapp" }); setSendTo(""); }} title="Send test WhatsApp">
                  <Phone size={16} className="text-[#22c55e]" />
                </Button>
                <Button variant="ghost" size="sm" className="p-2" onClick={() => openEdit(c)} title="Edit">
                  <Edit size={16} className="text-gray-600" />
                </Button>
                <Button variant="ghost" size="sm" className="p-2 hover:bg-red-50" onClick={() => remove(c.id)} title="Delete">
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>

      {/* Create/Edit Twilio dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="twilio-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Twilio Configuration" : "Add Twilio Configuration"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div className="space-y-2">
              <Label>Config Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Primary Twilio" data-testid="twilio-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Account SID</Label>
              <Input value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} required placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="twilio-sid-input" />
            </div>
            <div className="space-y-2">
              <Label>Auth Token {editingId && <span className="text-xs text-gray-400">(leave blank to keep existing)</span>}</Label>
              <Input type="password" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} required={!editingId} data-testid="twilio-token-input" />
            </div>
            <div className="space-y-2">
              <Label>SMS From Number <span className="text-xs text-gray-400">(E.164, e.g. +15551234567)</span></Label>
              <Input value={form.sms_from} onChange={(e) => setForm({ ...form, sms_from: e.target.value })} placeholder="+15551234567" data-testid="twilio-sms-from-input" />
            </div>
            <div className="space-y-2">
              <Label>WhatsApp From <span className="text-xs text-gray-400">(prefix with whatsapp:)</span></Label>
              <Input value={form.whatsapp_from} onChange={(e) => setForm({ ...form, whatsapp_from: e.target.value })} placeholder="whatsapp:+14155238886" data-testid="twilio-wa-from-input" />
              <p className="text-xs text-gray-500"><code>whatsapp:+14155238886</code> is the Twilio sandbox default; for production, use your approved WhatsApp sender.</p>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1fa7a1] hover:bg-[#178a85]" data-testid="twilio-submit-btn">
                {editingId ? "Save Changes" : "Add Twilio"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send test message dialog */}
      <Dialog open={!!sendDialog} onOpenChange={(o) => !o && setSendDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Test {sendDialog?.channel === "whatsapp" ? "WhatsApp" : "SMS"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-2">
              <Label>Recipient Phone (E.164)</Label>
              <Input type="tel" placeholder="+15551234567" value={sendTo} onChange={(e) => setSendTo(e.target.value)} data-testid="test-phone-input" />
              {sendDialog?.channel === "whatsapp" && (
                <p className="text-xs text-amber-600">⚠ Recipient must have joined the Twilio WhatsApp sandbox first.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Message Body</Label>
              <Input value={sendBody} onChange={(e) => setSendBody(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(null)}>Cancel</Button>
            <Button className="bg-[#1fa7a1] hover:bg-[#178a85]" onClick={submitSendTest} data-testid="twilio-send-confirm-btn">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const VoiceStudio = () => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", file: null });
  const [busy, setBusy] = useState(false);

  const fetchVoices = async () => {
    setLoading(true);
    try {
      const res = await getVoices();
      setVoices(res.data.voices);
      setConfigured(!!res.data.configured);
    } catch { toast.error("Failed to load voices"); }
    setLoading(false);
  };

  useEffect(() => { fetchVoices(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file || !form.name) { toast.error("Provide a name + audio sample"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("file", form.file);
      const res = await cloneVoice(fd);
      if (res.data.mocked) {
        toast.success("Voice recorded in mock mode — add ELEVENLABS_API_KEY to clone for real");
      } else {
        toast.success(`Voice cloned: ${res.data.voice_id}`);
      }
      setOpen(false);
      setForm({ name: "", description: "", file: null });
      fetchVoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clone failed");
    }
    setBusy(false);
  };

  return (
    <Card className="border border-gray-100 shadow-sm" data-testid="voice-studio-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <MessageSquare size={20} className="text-[#f59e0b]" />
            Voice Studio (ElevenLabs)
            {!configured && <Badge className="bg-amber-100 text-amber-700 text-xs">MOCK MODE</Badge>}
          </CardTitle>
          <Button size="sm" onClick={() => setOpen(true)} className="bg-[#f59e0b] hover:bg-[#d97706] text-white gap-2" data-testid="clone-voice-btn">
            <Plus size={16} /> Clone Voice
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!configured && (
          <div className="p-3 border border-amber-200 bg-amber-50 rounded-lg text-xs text-amber-800">
            ⚠️ ElevenLabs is not configured. Add <code className="bg-white px-1 rounded">ELEVENLABS_API_KEY=...</code> to <code>/app/backend/.env</code> and restart the backend to enable real voice cloning + TTS. Get a key at <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noreferrer" className="underline">elevenlabs.io</a>.
          </div>
        )}
        {loading ? (
          <p className="text-sm text-gray-500">Loading voices...</p>
        ) : Array.isArray(voices) && voices.length === 0 ? (
          <p className="text-sm text-gray-500 p-4 border border-dashed rounded text-center">No voices yet. Clone one above.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Array.isArray(voices) && voices.map((v) => (
              <div key={v.voice_id} className="p-3 border rounded-lg flex items-center justify-between bg-white" data-testid={`voice-${v.voice_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{v.name}</p>
                  <p className="text-xs text-gray-400">{v.category} {v.mocked && "(mock)"}</p>
                </div>
                {v.preview_url && (
                  <audio controls src={v.preview_url} className="h-8" />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="clone-dialog">
          <DialogHeader><DialogTitle>Clone a Voice</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div className="space-y-2">
              <Label>Voice Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Executive Voice" required data-testid="voice-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Male, mid-40s, calm authoritative" />
            </div>
            <div className="space-y-2">
              <Label>Audio Sample (30-60s recommended, mp3/wav/m4a)</Label>
              <Input type="file" accept="audio/*" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} required data-testid="voice-file-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#f59e0b] hover:bg-[#d97706] text-white" data-testid="voice-submit-btn">
                {busy ? "Cloning..." : "Clone"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const Settings = () => {
  const { user, setUser, logout } = useAuth();

  // Profile editable fields
  const [name, setName] = useState("");
  const [department, setDepartment] = useState("");
  const [saving, setSaving] = useState(false);

  // Password change fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
    setDepartment(user?.department || "");
  }, [user]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Full name is required"); return; }
    setSaving(true);
    try {
      await updateUser(user.id, {
        name: name.trim(),
        department: department.trim(),
      });
      setUser((prev) => ({ ...prev, name: name.trim(), department: department.trim() }));
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    }
    setSaving(false);
  };

  // Change password handler — auto logout after success
  const handleChangePassword = async () => {
  if (!currentPassword || !newPassword || !confirmPassword) {
    toast.error("Please fill all password fields");
    return;
  }
  if (newPassword !== confirmPassword) {
    toast.error("New password and confirm password do not match");
    return;
  }
  if (newPassword.length < 8) {
    toast.error("New password must be at least 8 characters");
    return;
  }

  setChangingPassword(true);
  try {
    await changePassword({
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    toast.success("Password updated successfully. Please login again.");

    // Turant logout — koi delay nahi
    setTimeout(() => {
      logout();
    }, 1000);
  } catch (err) {
    toast.error(err.response?.data?.detail || "Failed to update password");
    setChangingPassword(false);
  }
};

  useEffect(() => {
    getMe()
      .then((res) => setUser(res.data))
      .catch(() => toast.error("Failed to load profile"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-[#8c3add]/10 text-[#8c3add]";
      case "manager": return "bg-[#1fa7a1]/10 text-[#1fa7a1]";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile Card */}
      <Card className="border border-gray-100 shadow-sm" data-testid="profile-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <User size={20} className="text-[#8c3add]" />
            Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar Section */}
          <div className="flex items-center gap-6">
            <Avatar className="h-20 w-20 border-4 border-[#8c3add]/20">
              <AvatarFallback className="bg-[#8c3add] text-white text-2xl font-bold">
                {user?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-xl font-semibold text-[#1f1f23]">{user?.name}</h3>
              <p className="text-gray-500">{user?.email}</p>
              <Badge className={`mt-2 capitalize ${getRoleBadgeColor(user?.role)}`}>
                {user?.role}
              </Badge>
            </div>
          </div>

          <Separator />

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User size={16} className="text-gray-400" />
                Full Name
              </Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" />
            </div>

            {/* <div className="space-y-2">
              <Label htmlFor="department" className="flex items-center gap-2">
                <Building size={16} className="text-gray-400" />
                Department
              </Label>
              <Input id="department" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="Not set" data-testid="settings-department" />
            </div> */}
            <div className="space-y-2">
              <Label htmlFor="role" className="flex items-center gap-2">
                <Shield size={16} className="text-gray-400" />
                Role
              </Label>
              <Input id="role" value={user?.role || ""} disabled readOnly className="capitalize" data-testid="settings-role" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail size={16} className="text-gray-400" />
                Email Address
              </Label>
              <Input id="email" type="email" value={user?.email || ""} disabled readOnly data-testid="settings-email" />
            </div>
          </div>

          <Button
            className="bg-[#8c3add] hover:bg-[#702eb0] text-white gap-2"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-profile-btn"
          >
            <Save size={16} />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>

      {/* SMTP Configurations */}
      <SmtpSettings />

      {/* Twilio Configurations (SMS + WhatsApp) */}
      <TwilioSettings />

      {/* Voice Studio */}
      <VoiceStudio />

      {/* Notifications */}
      <Card className="border border-gray-100 shadow-sm" data-testid="notifications-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Bell size={20} className="text-[#8c3add]" />
            Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            { id: "email_alerts", label: "Email Alerts", desc: "Receive alerts via email", default: true },
            { id: "campaign_updates", label: "Campaign Updates", desc: "Notifications for campaign status changes", default: true },
            { id: "risk_alerts", label: "Risk Alerts", desc: "High-risk user notifications", default: true },
            { id: "weekly_digest", label: "Weekly Digest", desc: "Summary of weekly activities", default: false }
          ].map((setting) => (
            <div key={setting.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-[#1f1f23]">{setting.label}</p>
                <p className="text-sm text-gray-500">{setting.desc}</p>
              </div>
              <Switch defaultChecked={setting.default} />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Security */}
      <Card className="border border-gray-100 shadow-sm" data-testid="security-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Lock size={20} className="text-[#8c3add]" />
            Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password">Current Password</Label>
            <Input
              id="current_password"
              type="password"
              placeholder="••••••••"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="current-password-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleChangePassword}
            disabled={changingPassword}
            data-testid="update-password-btn"
          >
            {changingPassword ? "Updating..." : "Update Password"}
          </Button>
        </CardContent>
      </Card>

      {/* Appearance */}
      {/* <Card className="border border-gray-100 shadow-sm" data-testid="appearance-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Palette size={20} className="text-[#8c3add]" />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div>
              <p className="font-medium text-[#1f1f23]">Theme</p>
              <p className="text-sm text-gray-500">Choose your preferred theme</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="border-[#8c3add] text-[#8c3add]">
                Light
              </Button>
              <Button variant="outline" size="sm">Dark</Button>
              <Button variant="outline" size="sm">System</Button>
            </div>
          </div>
        </CardContent>
      </Card> */}

      {/* Danger Zone */}
      <Card className="border border-red-100 shadow-sm" data-testid="danger-zone-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-red-600">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
            <div>
              <p className="font-medium text-red-700">Sign Out</p>
              <p className="text-sm text-red-600/70">Sign out from your account</p>
            </div>
            <Button
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut size={16} className="mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;